import type { ComponentProps, HTMLAttributeAnchorTarget } from 'react'
import { useMemo } from 'react'
import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import CruisePriceDetails from './CruisePriceDetails'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import CruisesUrgencyTags from 'components/Cruises/CruisesUrgencyTags'
import { getCruiseOfferPriceVariationNew } from 'lib/cruises/cruiseUtils'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import CruiseInclusionListHomeTile from 'components/Cruises/CruiseInclusion/CruiseInclusionListHomeTile'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'
import { getIsEnabledLuxPlusCruiseInclusions } from 'luxPlus/selectors/featureToggle'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { useIntl } from 'react-intl'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.CruiseOffer
  onClick: () => void
  className?: string
  offerUrl: string
  // Temporary for PR #33303: only used by ChatTripPlanner small cruise cards until
  // full cruise support lands. Remove around 2026-05-12 if that support is live.
  offerLinkTarget?: HTMLAttributeAnchorTarget
  size: 'small' | 'medium'
}

function CruiseOfferCard(props: Props) {
  const intl = useIntl()
  const {
    offer,
    onClick,
    className,
    offerUrl,
    offerLinkTarget,
    size,
    variant,
    eagerLoadImage,
  } = props
  const { lowestOverallPriceDetails } = offer.mainDepartureDetails

  const priceDetails = useMemo(() => {
    const priceVariation =
      getCruiseOfferPriceVariationNew(offer, lowestOverallPriceDetails)

    return priceVariation.primaryPrice
  }, [offer, lowestOverallPriceDetails])

  const isUltraLux = isUltraLuxOffer(offer)
  const view = useCruiseOfferView({ offer })
  const {
    luxPlusInclusions,
    isSpecialOffer,
    evergreenInclusions,
    tileInclusions,
    standardInclusions,
    memberLowestPrice,
    canSeeLuxPlusLabel,
  } = view
  const isEnabledLuxPlusCruiseInclusions = useAppSelector(getIsEnabledLuxPlusCruiseInclusions)

  const inclusionsElement = useMemo(() => (
    <CruiseInclusionListHomeTile
      evergreenInclusions={evergreenInclusions}
      tileInclusions={tileInclusions}
      standardInclusions={standardInclusions}
      luxPlusInclusions={luxPlusInclusions}
    />
  ), [evergreenInclusions, tileInclusions, standardInclusions, luxPlusInclusions])
  const showLuxPlusLabel = isEnabledLuxPlusCruiseInclusions && offer.luxPlus.hasMemberInclusions

  const specialOfferLabel = intl.formatMessage({
    id: 'if0bcPpT5eaahDeyjgEG',
    defaultMessage: 'Special offer',
    description: 'Badge label for cruise special offer on offer card',
  })

  let imageCornerLabel = isSpecialOffer ? <PromotionLabel variant="premium">{specialOfferLabel}</PromotionLabel> : undefined
  if (isUltraLux) {
    imageCornerLabel = <ProductTypeLabel productType={offer.productType} />
  }

  return (
    <ProductPaletteProvider product={offer}>
      {size === 'medium' && <CarouselCardMedium
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
        image={offer.images[0]}
        images={offer.images}
        title={offer.name}
        location={getLocationString(intl, offer)}
        to={offerUrl}
        providerName={offer.cruiseLine.name}
        onClick={onClick}
        urgencyLabels={<LabelGroup>
          {showLuxPlusLabel && <CruiseLuxPlusOnboardCreditLabel
            offer={offer}
            luxPlusInclusions={luxPlusInclusions}
          />}
          <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
          <CruisesUrgencyTags tags={offer.urgencyTags} />
        </LabelGroup>}
        priceDetails={
          priceDetails && <CruisePriceDetails
            offer={offer}
            offerId={offer.id}
            variant="condensed"
            priceDetails={priceDetails}
            memberPrice={memberLowestPrice?.primaryPrice.total}
            canSeeLuxPlusLabel={canSeeLuxPlusLabel}
          />
        }
        loyaltyPointsElement={<CruiseLoyaltyPoints
          offer={offer}
          calculationType="estimate"
        />}
        taglineInclusions={inclusionsElement}
        imageCornerLabel={imageCornerLabel}
      />}
      {size === 'small' && <CarouselCardSmall
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
        image={offer.images[0]}
        images={offer.images}
        location={getLocationString(intl, offer)}
        title={offer.name}
        to={offerUrl}
        target={offerLinkTarget}
        onClick={onClick}
        urgencyLabels={<LabelGroup>
          {showLuxPlusLabel && <CruiseLuxPlusOnboardCreditLabel
            offer={offer}
            luxPlusInclusions={luxPlusInclusions}
          />}
          <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
          <CruisesUrgencyTags tags={offer.urgencyTags} />
        </LabelGroup>}
        priceDetails={
          priceDetails && <CruisePriceDetails
            offerId={offer.id}
            offer={offer}
            priceDetails={priceDetails}
            memberPrice={memberLowestPrice?.primaryPrice.total}
            canSeeLuxPlusLabel={canSeeLuxPlusLabel}
          />
        }
        loyaltyPointsElement={<CruiseLoyaltyPoints
          offer={offer}
          calculationType="estimate"
        />}
        taglineInclusions={inclusionsElement}
        imageCornerLabel={imageCornerLabel}
      />}
    </ProductPaletteProvider>
  )
}

export default CruiseOfferCard
